import { Document } from "models/schemas";
import { Audit } from "models/generateAuditSchemas";
import { GaiaObject } from "models/generateGaiaObjSchemas";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";
import { ExtendedFilteringProperty } from "components/server-side-table/definitions";
import { VersionCollisionInfoOS } from "./version-utils-os";
import { commonPropertyFilterI18nStrings } from "utils/table-utils";
import { createServerSideDateOperators } from "utils/table-filtering-utils";

export type DocumentCategory =
  | "audits"
  | "notices"
  | "litigations"
  | "assessments"
  | "payments"
  | "tasks"
  | "events"
  | "team-files";

export interface GroupedDocumentRow {
  documentId: string;
  fileName: string;
  children: Document[];
}

export type DocumentTableItem = Document | GroupedDocumentRow;

export const isGroupedRow = (
  item: DocumentTableItem,
): item is GroupedDocumentRow => "children" in item;

/** Why Answers knowledge base is off for SharePoint files on approval teams. */
export const SHAREPOINT_KB_APPROVAL_COMING_SOON =
  "Knowledge base approval for SharePoint files is coming soon.";

// Property filter types
export interface PropertyFilterToken {
  propertyKey?: string; // Optional for free text search
  operator: string;
  value?: string;
  values?: string[];
}

export interface PropertyFilterQuery {
  tokens?: PropertyFilterToken[];
  operation: string;
  groups?: PropertyFilterQuery[];
}

export interface PaginationMetadata {
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  searchAfter?: string[];
}

export interface SearchDocumentRequest {
  teamName: string;
  parentId?: string;
  recursiveSearch?: boolean;
  propertyFilterQuery?: PropertyFilterQuery;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  searchAfter?: string[];
  excludeFields?: string[];
}

export interface SearchDocumentResponse {
  documents: Document[];
  paginationMetadata: PaginationMetadata;
}

export interface GetDocumentUniqueValuesRequest {
  teamName: string;
  fields: string[];
  limit?: number;
  parentId?: string;
  recursiveSearch?: boolean;
  propertyFilterQuery?: PropertyFilterQuery;
}

export interface GetDocumentUniqueValuesResponse {
  uniqueValues: Record<string, string[]>;
}

export interface IDocumentsTableOSProps {
  teamName: string;
  parentId: string;
  gaiaObjectId?: string;
  shortId?: string;
  category: DocumentCategory;
  showRelatedToColumn?: boolean;
  onFolderClick?: (folderName: string) => void;
  teamLevelTable?: boolean;
  displayMode?: "flat" | "grouped";
  previewEnabled?: boolean;
  /** Gaia object the table's documents hang off, already UNWRAPPED (pass
   *  `selectedGaiaObject.gaiaObject`, never the `{ gaiaObject }` wrapper). */
  gaiaObjectParent?: Audit | GaiaObject;
  /** Show "Import from SharePoint" (object Files tabs only). */
  showSharePointImport?: boolean;
  /** Team whose document types the upload and add-link modals offer. Defaults to the viewer's. */
  uiContentTeamName?: string;
}

// Column definitions props
export interface ColumnDefinitionsProps {
  onFolderClick?: (folder: Document) => void;
  onPreviewClick?: (doc: Document) => void;
  onActionClick?: (action: string, doc: Document) => void;
  onFileNameClick?: (doc: Document) => void;
  showRelatedToColumn?: boolean;
  getNameForId?: (id: string) => string | null;
  docToRename?: Document;
  fileName?: string;
  setFileName?: (name: string) => void;
  setDocToRename?: (doc: Document | undefined) => void;
  teamLevelTable?: boolean;
  isRecursiveSearch?: boolean;
  teamName?: string;
  currentParentId?: string;
  category?: DocumentCategory;
  displayMode?: "flat" | "grouped";
  previewEnabled?: boolean;
  isGaiaKBIngestionEnabled?: boolean;
  isAnswersKbEnabled?: boolean;
  kbStatusMap?: Record<string, DocumentKbStatus>;
  kbStatusLoading?: boolean;
}

// File Preview Split Panel props
export interface FilePreviewSplitPanelProps {
  documentId: string | null;
  isFilesTab?: boolean;
  gaiaObjectType?: string;
  teamName: string;
  parentId: string;
  onClose?: () => void;
}

// Version Confirmation Modal OS props
export interface VersionConfirmationModalOSProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  collisions: VersionCollisionInfoOS[];
  onConfirm: () => void;
}

// Server-side document filtering property type
export type ServerSideDocumentFilteringProperty = ExtendedFilteringProperty & {
  key: keyof Document;
};

// Property filter i18n strings for documents table
export const propertyFilterI18nStrings: PropertyFilterProps.I18nStrings = {
  ...commonPropertyFilterI18nStrings,
  filteringPlaceholder: "Find documents by name",
  tokenEditorAddNewTokenLabel: "Add new filter",
};

// Server-side filtering properties factory
export const createServerSideFilteringProperties = (
  teamLevelTable = false,
): ServerSideDocumentFilteringProperty[] => {
  const allProperties: ServerSideDocumentFilteringProperty[] = [
    {
      propertyLabel: "File name",
      key: "fileName",
      groupValuesLabel: "File names",
      operators: [":", "!:", "=", "!=", "^", "!^"],
      fieldType: "text",
    },
    {
      propertyLabel: "Extension type",
      key: "extensionType",
      groupValuesLabel: "Extension types",
      operators: [
        { operator: "=", tokenType: "enum" },
        { operator: "!=", tokenType: "enum" },
      ],
      fieldType: "enum",
    },
    {
      propertyLabel: "Document type",
      key: "documentType",
      groupValuesLabel: "Document types",
      operators: [
        { operator: "=", tokenType: "enum" },
        { operator: "!=", tokenType: "enum" },
      ],
      fieldType: "enum",
    },
    {
      propertyLabel: "Modified by",
      key: "modifiedBy",
      groupValuesLabel: "Modified by users",
      operators: [
        { operator: "=", tokenType: "enum" },
        { operator: "!=", tokenType: "enum" },
        ":",
        "!:",
      ],
      fieldType: "enum",
    },
    {
      propertyLabel: "Uploaded by",
      key: "createdBy",
      groupValuesLabel: "Uploaded by users",
      operators: [
        { operator: "=", tokenType: "enum" },
        { operator: "!=", tokenType: "enum" },
        ":",
        "!:",
      ],
      fieldType: "enum",
    },
    {
      propertyLabel: "Last updated date",
      key: "modifiedAt",
      groupValuesLabel: "Last updated date values",
      operators: createServerSideDateOperators(),
      fieldType: "date",
    },
    {
      propertyLabel: "Uploaded on date",
      key: "createdAt",
      groupValuesLabel: "Uploaded on date values",
      operators: createServerSideDateOperators(),
      fieldType: "date",
    },
  ];

  // Filter out documentType for team level tables
  return teamLevelTable
    ? allProperties.filter((prop) => prop.key !== "documentType")
    : allProperties;
};

// GetDocument types
export interface GetDocumentInput {
  documentId: string;
}

export interface GetDocumentOutput {
  document: Document;
}

export interface RegisterDocument {
  fileName: string;
  extensionType: string;
  parentId: string;
  teamName: string;
  gaiaObjectId?: string;
  s3Key?: string;
  size?: string;
  documentType: string;
  securityLevel: string;
  externalUrl?: string;
}

export interface BatchRegisterDocumentsInput {
  documents: RegisterDocument[];
}

export interface BatchRegisterDocumentsOutput {
  succeededDocuments: string[];
  failedDocuments: string[];
  invalidatedKBApprovalDocuments?: boolean;
  requiresReIngestion?: boolean;
}

export interface DocumentUpdate {
  documentId: string;
  fileName?: string;
  documentType?: string;
  securityLevel?: string;
  parentId?: string;
  description?: string;
}

export interface BatchUpdateDocumentsInput {
  updates: DocumentUpdate[];
}

export interface BatchUpdateDocumentsOutput {
  succeededDocuments: string[];
  failedDocuments: string[];
  invalidatedKBApprovalDocuments?: boolean;
  requiresReIngestion?: boolean;
}

export interface DocumentDeletion {
  documentId: string;
  allVersions: boolean;
  versionId?: string;
}

export interface BatchDeleteDocumentsInput {
  deletions: DocumentDeletion[];
}

export interface BatchDeleteDocumentsOutput {
  succeededDocuments: string[];
  failedDocuments: string[];
  invalidatedKBApprovalDocuments?: boolean;
  requiresReIngestion?: boolean;
}

export interface CreateTeamFilesImportInput {
  teamName: string;
  parentId: string;
  zipS3Key: string;
}

export interface CreateTeamFilesImportOutput {
  queryId: string;
}

export interface CreateTeamFilesExportInput {
  teamName: string;
  parentId: string;
  includeAllVersions: boolean;
  exportName?: string;
}

export interface CreateTeamFilesExportOutput {
  queryId: string;
}

export interface DownloadUrlRequest {
  documentId: string;
  preview: boolean;
  versionId?: string;
  fileIndex?: number;
}

export interface DownloadUrlResponse {
  url: string;
  bucketOwner: string;
}

export interface UploadUrlRequest {
  prefix: string;
  fileName: string;
  teamName: string;
  securityLevel: string;
  uniqueKey?: string;
}

export interface UploadUrlResponse {
  url: string;
  bucketOwner: string;
  uniqueKey: string;
}

export interface TeamFilesExportModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  folderName: string;
  onConfirm: (includeAllVersions: boolean) => void;
  folderCount?: number;
}

// KB Status Lazy Load types
export interface DocumentKbKeyEntry {
  documentId: string;
  kbS3Key?: string;
  docKbS3Key?: string;
  atlasObjectKey?: string;
}

export interface GetDocumentsKbStatusRequest {
  teamName: string;
  documents: DocumentKbKeyEntry[];
}

export interface DocumentKbStatus {
  kbIndexingStatus?: string;
  answersKbIndexingStatus?: string;
}

export interface GetDocumentsKbStatusResponse {
  statuses: Record<string, DocumentKbStatus>;
}

// Team Files import ZIP structural pre-check
export const ZIP_EOCD_RECORD_BYTES = 22;

// Must match `zipfile._EndRecData`'s own EOCD search range, `max(filesize - (1 << 16) - 22, 0)`, not the spec's 22 + 65535.
export const ZIP_EOCD_SEARCH_WINDOW_BYTES = ZIP_EOCD_RECORD_BYTES + 65536;

export const ZIP64_UINT16_SENTINEL = 0xffff;
export const ZIP64_UINT32_SENTINEL = 0xffffffff;

/** The two reasons carry different user copy. */
export type ZipStructureResult =
  | { valid: true }
  | { valid: false; reason: "not-a-zip" | "truncated" };

/**
 * Marker `uploadZipForImport` returns when it has already shown the user a
 * specific, actionable error of its own and the caller must add nothing further.
 */
export interface UploadReportedToUser {
  reportedToUser: true;
}

/**
 * What `uploadZipForImport` resolves to: an S3 key on success, or the
 * already-reported marker on every failure.
 */
export type UploadZipForImportResult =
  | { zipS3Key: string }
  | UploadReportedToUser;
